import Cookies from "js-cookie";
import { createContext, useContext, useEffect, useState } from "react";
import axiosInstance from "../config/axiosInstance";
import useAlert from "../hooks/Alert";

const userThemeCookieName = "wafaa-user-data-theme";

const AppSettingContext = createContext();

export function useAppSetting() {
    return useContext(AppSettingContext);
}

const getDomainDetails = (url) => {
    try {
        // Step 1: Extract the part after `://`
        let domain = url;
        if (domain.includes("192") || domain.includes("localhost") || domain.includes("g71gd5bd-3000") || domain.includes("1jmp06cp")) return { SUB_DOMAIN_ID: "devp", DOMAIN_ID: "devp" };
        if (url.includes("://")) {
            domain = url.split("://")[1];
        }

        // Step 2: Remove any port number (if present)
        domain = domain.split(":")[0];

        // Step 3: Split the domain into parts
        const domainParts = domain.split(".");
        if (domainParts.length === 2) {
            // Case 1: Simple domain like "abc.com"
            return { SUB_DOMAIN_ID: domainParts[0], DOMAIN_ID: domainParts[0] };
        } else if (domainParts.length > 2) {
            // Case 2: Subdomain present like "web.mycomsys.com"
            return { SUB_DOMAIN_ID: domainParts[0], DOMAIN_ID: domainParts[1] };
        } else {
            // Fallback case
            return { SUB_DOMAIN_ID: "demo", DOMAIN_ID: "demo" };
        }
    } catch (error) {
        // Fallback in case of errors
        return { SUB_DOMAIN_ID: "demo", DOMAIN_ID: "demo" };
    }
};

// Example usage
// console.log(getDomainDetails("abc.com")); // { SUB_DOMAIN_ID: 'abc', DOMAIN_ID: 'abc' }
// console.log(getDomainDetails("web.mycomsys.com")); // { SUB_DOMAIN_ID: 'web', DOMAIN_ID: 'mycomsys' }
// console.log(getDomainDetails("http://192.168.0.1")); // { SUB_DOMAIN_ID: 'demo', DOMAIN_ID: 'demo' }

export function AppSettingProvider(props) {
    const [appSettings, setAppSettings] = useState({});

    const [clientAccess, setClientAccess] = useState([]);

    const [theme, setTheme] = useState(Cookies.get(userThemeCookieName) ?? "light");

    const { setShowAlert, setAlertStyle, setAlertMessage, hideAlert } = useAlert();

    useEffect(() => {
        fetchBaseURL();
    }, []);

    const fetchBaseURL = async () => {
        try {
            const { SUB_DOMAIN_ID, DOMAIN_ID } = getDomainDetails(new URL(window.location.href).origin);

            const payLoad = {
                FUNCTION: "FileReader",
                APP_ID: "TrueValue",
                VERSION: "V1.0",
                DOMAIN_ID: DOMAIN_ID,
                SUB_DOMAIN_ID: SUB_DOMAIN_ID,
                FileName: `LoyaltyApplication/settings/${SUB_DOMAIN_ID}/appconfig.json`,
            };

            const response = await axiosInstance.post("https://web2.mycomsys.com:8803/api/appconfig/v1/GetAppSettings", payLoad, {
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (response?.data?.AppBaseURL) {
                setAppSettings(response.data);
                await fetchClientAccess(response.data);
                return response.data?.AppBaseURL;
            } else {
                if (SUB_DOMAIN_ID.includes("localhost") || SUB_DOMAIN_ID.includes("192")) {
                    fetchBaseURLRetryDefault();
                } else {
                    showErrorAlert("Failed to get app settings", "App Setting");
                }
            }
        } catch (error) {
            console.error("Error fetching app settings:", error);
            showErrorAlert("Failed to fetch app settings. Please try again.", "App Setting");
        }

        setTheme(Cookies.get(userThemeCookieName) ?? "light");
    };

    const showErrorAlert = (message, mode) => {
        hideAlert();
        setAlertStyle({
            background: "linear-gradient(to right, #f6743e 0%, #d42525 100%)",
        });
        setAlertMessage({
            success: false,
            message: message,
            mode: mode,
        });
        setShowAlert("d-block");
    };

    const fetchClientAccess = async (settingsResponse) => {
        const payLoad = {
            DATA: {
                CLIENT_ID: settingsResponse.ClientId,
            },
        };

        try {
            // Axios already handles JSON parsing, so no need for .json()
            const response = await axiosInstance.post(settingsResponse.GetAccess, payLoad, {
                headers: {
                    "Content-Type": "application/json",
                },
            });

            // Assuming the response contains the necessary data
            setClientAccess(response.data?.DATA);
        } catch (error) {
            console.error("Error fetching client access:", error);
        }
    };

    const fetchBaseURLRetryDefault = async () => {
        try {
            const { SUB_DOMAIN_ID, DOMAIN_ID } = getDomainDetails(new URL("https://demo.elvisben.me.ke").origin);

            const payLoad = {
                FUNCTION: "FileReader",
                APP_ID: "TrueValue",
                VERSION: "V1.0",
                DOMAIN_ID: DOMAIN_ID,
                SUB_DOMAIN_ID: SUB_DOMAIN_ID,
                FileName: `LoyaltyApplication/settings/${SUB_DOMAIN_ID}/appconfig.json`,
            };

            const response = await axiosInstance.post("https://web2.mycomsys.com:8803/api/appconfig/v1/GetAppSettings", payLoad, {
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (response.data?.AppBaseURL) {
                setAppSettings(response.data);
                await fetchClientAccess(response.data);
            }
        } catch (error) {
            console.error("Error in fetchBaseURLRetryDefault:", error);
            showErrorAlert("Retry failed to fetch app settings. Please try again later.", "App Setting Retry");
        }
    };

    return <AppSettingContext.Provider value={{ appSettings, clientAccess, theme, setTheme, fetchBaseURL }}>{props.children}</AppSettingContext.Provider>;
}
